{/*
  * ORTBCreativeTypesPage.tsx
  * https://dash.applovin.com/documentation/mediation/auctions/dsps/ortb-spec/creative-types
  * Documentation > MAX Demand Partners > DSPs > ORTB Specification > Creative Types
  */}
import React, { FunctionComponent } from 'react';
import { DataTable, DocsPageModule, IndentWidthType, LabelFontFamily } from 'UI-Dash-Commons-UI';
import CodeSnippet from '../containers/CodeSnippetContainer';
import styled from 'styled-components';
import {
    nativeRequestAssetData,
    nativeRequestInformationData,
} from '../redux/constants';

type ORTBCreativeTypesPageProps = {
    linkTargetTopPosition?: number;
}

const ORTBCreativeTypesPage: FunctionComponent<ORTBCreativeTypesPageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <DocsPageModule.DocsPageHeader text={'Creative Types'} type={DocsPageModule.DocsPageHeaderType.HEADING_1}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'The following details on creative types apply to DSP bidders in the AppLovin Exchange. '
                                                    + 'When you introduce a new creative format, AppLovin highly encourages that you test with the '} />
                <DocsPageModule.DocsPageContentAnchor
                    to={'/documentation/mediation/auctions/dsps/testing'}
                    text={'AppLovin DSP Sample App'}
                    newWindow={false}
                />
                <DocsPageModule.DocsPageContentText text={' before you go live.'} />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContent text={`These specifications are ad format agnostic and apply to all ad types supported on AppLovin.`} />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Note: '} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI} />
                <DocsPageModule.DocsPageContentText
                    text={'SDK bidders must handle the rendering of creatives via their own SDK and should reach out to their AppLovin contact should they have any questions. '}
                />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'VAST Support'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Inventory that supports '} />
                <DocsPageModule.DocsPageContentAnchor to={'https://www.iab.com/guidelines/vast/'} text={'VAST'} newWindow={true} />
                <DocsPageModule.DocsPageContentText text={' passes '} />
                <DocsPageModule.DocsPageContentAnchor to={'/documentation/mediation/auctions/dsps/ortb-spec/bid-request#video-object'}
                                                      text={'the imp.video object'} newWindow={false} />
                <DocsPageModule.DocsPageContentText text={' in the '} />
                <DocsPageModule.DocsPageContentAnchor to={'/documentation/mediation/auctions/dsps/ortb-spec/bid-request'}
                                                      text={'bid request'} newWindow={false} />
                <DocsPageModule.DocsPageContentText text={'. VAST inventory complies with MRAID. '} />

            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent text={'The VAST creative must be completely secure, including all tracking and redirect URLs.'} />
            <DocsPageModule.DocsPageContent text={'VAST is supported in fullscreen placements only (interstitial and rewarded video). '
                                                + 'AppLovin aspect-fits the video in the middle of the screen, which depends on the device size. '
                                                + 'Both landscape-oriented and portrait-oriented video are supported.' } />
            <DocsPageModule.DocsPageContent
                text={'VAST videos auto-play on Android and iOS. '
                    + 'AppLovin supports only Linear videos. '
                    + 'Videos should be at least 5 seconds long and at most 30 seconds long. '
                    + '(Rewarded videos should be between 20 and 30 seconds long, otherwise they may be rejected.) '
                    + 'Video file sizes should be below 4MB (for optimal playback, AppLovin recommends video files to be smaller than 2MB). '
                    + 'Video files larger than 2MB can perform poorly. '
                    + 'If the request to retrieve the video file exceeds 30 seconds, it is considered a timeout.'}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'The '}/>
                <DocsPageModule.DocsPageContentCode children={'flv'}/>
                <DocsPageModule.DocsPageContentText text={' and '}/>
                <DocsPageModule.DocsPageContentCode children={'swf'}/>
                <DocsPageModule.DocsPageContentText text={' media MIME types are not supported. The following MIME types are supported:'}/>
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Android: '} fontFamily={LabelFontFamily.FONT_FAMILY_MEDIUM}/>
                        <DocsPageModule.DocsPageContentCode children={'video/mp4'}/>
                        <DocsPageModule.DocsPageContentText text={', '}/>
                        <DocsPageModule.DocsPageContentCode children={'video/3gpp'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'iOS: '} fontFamily={LabelFontFamily.FONT_FAMILY_MEDIUM}/>
                        <DocsPageModule.DocsPageContentCode children={'video/mp4'}/>
                        <DocsPageModule.DocsPageContentText text={', '}/>
                        <DocsPageModule.DocsPageContentCode children={'video/3gpp'}/>
                        <DocsPageModule.DocsPageContentText text={', '}/>
                        <DocsPageModule.DocsPageContentCode children={'video/3gpp2'}/>
                        <DocsPageModule.DocsPageContentText text={', '}/>
                        <DocsPageModule.DocsPageContentCode children={'video/quicktime'}/>
                        <DocsPageModule.DocsPageContentText text={', '}/>
                        <DocsPageModule.DocsPageContentCode children={'video/x-m4v'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin supports VAST 2.0, 3.0, 4.0, 4.1, and 4.2. '
                                                        + 'VAST-capable inventory supports both VAST InLine and Wrapper formats. '
                                                        + 'VPAID is not supported. The ' }/>
                <DocsPageModule.DocsPageContentAnchor to={'/documentation/mediation/auctions/dsps/ortb-spec/bid-response#bid-object'}
                                                      text={'bid.adm'} newWindow={false} />
                <DocsPageModule.DocsPageContentText text={' (or ad markup field) must contain the entire VAST XML payload '
                                                        + 'AppLovin does not support URL-encoded VAST tags.'} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'VAST-capable inventory supports companion (endcard) creative. '
                + 'MRAID companion (endcard) creative is supported. '
                + 'When multiple companion resources are sent, AppLovin uses the following order (if the first one fails, it tries the next one, and so on):'} />
                <DocsPageModule.DocsPageOrderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentCode children={'StaticResource'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentCode children={'IFrameResource'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentCode children={'HTMLResource'}/>
                        <DocsPageModule.DocsPageContentText text={' (by default, support is disabled for '} />
                        <DocsPageModule.DocsPageContentCode children={'HTMLResource'}/>
                        <DocsPageModule.DocsPageContentText text={' as a companion ad)'} />
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageOrderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Impression Tracking'} type={DocsPageModule.DocsPageHeaderType.HEADING_3}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin fires tags on the server side that you send through the '} />
                <DocsPageModule.DocsPageContentCode children={'<impression>'}/>
                <DocsPageModule.DocsPageContentText text={' node. You can send more than one such node.'} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Click Tracking'} type={DocsPageModule.DocsPageHeaderType.HEADING_3}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin fires the tag on the client side that you send through the '} />
                <DocsPageModule.DocsPageContentCode children={'<ClickTracking>'}/>
                <DocsPageModule.DocsPageContentText text={' node. If you use a deep-link, you must include it in the '} />
                <DocsPageModule.DocsPageContentCode children={'<ClickThrough>'}/>
                <DocsPageModule.DocsPageContentText text={' node.'} />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'If you use a deep-link in an iOS ad, the '} />
                <DocsPageModule.DocsPageContentCode children={'ext.skadn'}/>
                <DocsPageModule.DocsPageContentText text={' object must be '} />
                <DocsPageModule.DocsPageContentCode children={'null'}/>
                <DocsPageModule.DocsPageContentText text={'. Otherwise the App Store will be opened within the ad and the '} />
                <DocsPageModule.DocsPageContentCode children={'<ClickThrough>'}/>
                <DocsPageModule.DocsPageContentText text={' node will be dismissed.'} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Companion Ads'} type={DocsPageModule.DocsPageHeaderType.HEADING_3}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'AppLovin RTB expects a full-screen companion ad in the response for VAST ads. '
                    + 'The request specifies the phone screen dimensions. '
                    + 'The AppLovin SDK aspect-fits the ad in the middle of the screen. '
                    + 'Since these are video end cards, use something in the ~1.33 aspect ratio range (e.g. 1024×768 for landscape) and high quality images for best results with companion ads.'}/>
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin supports only '}/>
                <DocsPageModule.DocsPageContentCode children={'<HTMLResource>'}/>
                <DocsPageModule.DocsPageContentText text={' and '}/>
                <DocsPageModule.DocsPageContentCode children={'<StaticResource>'}/>
                <DocsPageModule.DocsPageContentText text={'. Support for '}/>
                <DocsPageModule.DocsPageContentCode children={'<HTMLResource>'}/>
                <DocsPageModule.DocsPageContentText text={' as a companion ad is off by default. '} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Example VAST Ad Markup'} type={DocsPageModule.DocsPageHeaderType.HEADING_3}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'javascript',
                    code: `{
  "adm": "<VAST version=\\"2.0\\">
    <Ad id=\\"applovin-video\\">
      <InLine>
        <AdSystem version=\\"AppLovin\\">AppLovin Ad Server</AdSystem>
        <AdTitle><![CDATA[AppLovin Video]]></AdTitle>
        <Description><![CDATA[AppLovin Video]]></Description>
        <Impression id=\\"AL_Imp\\" />
        <Creatives>
          <Creative sequence=\\"1\\" id=\\"1\\">
            <Linear>
              <Duration>00:00:13</Duration>
              <VideoClicks>
                <ClickThrough id=\\"AL_Click\\"><![CDATA[https://rtb.applovin.com/redirect?clcode=3!1563.1466706706!FgcnDB8CXKyVimq2aZHmpR2JxNmwsN1NMK7UlK6FqhOI54WxpITXiYcxUEVgXRIwedrcfVmD_DpD3lfBLqzpw5qF7sPXmXQ_kY1NLKQ2kdQn4cwctkm--RBq1Cmz5mX8RJRytc-G3KeJ9usTmgmXTSFtLvHGSGHIM7Pwfq_KiEAQiKweR20lp_iyQXVNwiUJmpFFoQpAm9bNU5JegcmPfIIBnesZKk5pmdJDpUEnjlFdNAqRCzci_qO-f6RtTdYEvJBqnh9usSgEdxnfLh-hHpNx5CovcfTejFfF94CTaTV_K6wEHXQOjZdT-yfdeydAQEkl3bUxiT8cXrEuCu73WHFVOoYwoLJLLAipuLCNCkj9DSvZ6JTnoQkPRlpBjSJQmmYXNv2HDKXYKSgIA-g-wRaTuRZ0lMi4OHNBK_N4ClACeg2TOFYTp3cHWnh6gEr-ZfWQ4FUzgGCLQRXm0Ez7wjCRM0vfoVsdVEZrctobQI7_im9tT4UmTjNgxP7dKlLHRwhl68fBqGUMobFSsohmuucakp5BXlSPyQIh91MoTOOsbR2UTMZXjFholy7qDgDxNxKzaQk0AFX6Vf0w**&did=104934ef84eee135bb&eventid=7d2a138bf9102f05dcbf9540b5f5dd2a317cc&ssl=1]]></ClickThrough>
              </VideoClicks>
              <MediaFiles>
                <MediaFile delivery=\\"progressive\\" bitrate=\\"932\\" width=\\"570\\" height=\\"320\\" type=\\"video/mp4\\"><![CDATA[https://vid.applovin.com/468779_570x320.mp4]]></MediaFile>
              </MediaFiles>
            </Linear>
          </Creative>
          <Creative sequence=\\"1\\" id=\\"1\\">
            <CompanionAds>
              <Companion width=\\"1024\\" height=\\"768\\">
                <StaticResource creativeType=\\"image/gif\\"><![CDATA[https://img.applovin.com/1461768_1024x768.gif]]></StaticResource>
                <CompanionClickThrough><![CDATA[https://rtb.applovin.com/redirect?clcode=3!1563.1466706806!FgcnDB8CXKHpfIyVimq2aZHmpR2JxNmwsN1NMK7UlK6FqhOI54WxpITXiYcxUEVgXRIwedrcfVmD_DpD3lfBLqzpw5qF7sPXmXQ_kY1NLKQ2kdQn4cwctkm--RBq1Cmz5mX8RJRytc-G3KeJ9usTmgmXTSFtLvHGSGHIM7Pwfq_KiEAQiKweR20lp_iyQXVNwiUJmpFFoQpAm9bNU5JegcmPfIIBnesZKk5pmdJDpUEnjlFdNAqRCzci_qO-f6RtTdYEvJBqnh9usSgEdxnfLh-hHpNx5CovcfTejFfF94CTaTV_K6wEHXQOjZdT-yfdeydAQEkl3bUxiT8cXrEuCu73WHFVOoYwoLJLLAipuLCNCkj9DSvZ6JTnoQkPRlpBjSJQmmYXNv2HDKXYKSgIA-g-wRaTuRZ0lMi4OHNBK_N4ClACeg2TOFYTp3cHWnh6gEr-ZfWQ4FUzgGCLQRXm0Ez7wjCRM0vfoVsdVEZrctobQI7_im9tT4UmTjNgxP7dKlLHRwhl68fBqGUMobFSsohmuucakp5BXlSPyQIh91MoTOOsbR2UTMZXjFholy7qDgDxNxyGKzaQk0AFX6Vf0w**&did=104924ef84eee135bb&eventid=7d82abf9102f05dcbf9540b5f5dd2a317cc&ssl=1]]></CompanionClickThrough>
              </Companion>
            </CompanionAds>
          </Creative>
        </Creatives>
        <Extensions>
        </Extensions>
      </InLine>
    </Ad>
  </VAST>"
}`
                } ]}
            />

            <DocsPageModule.DocsPageHeader text={'MRAID Support'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'MRAID ads must include a loading indicator to signal that the ad is loading. '
                    + 'There is no limit on ad duration for regular MRAID ads, but rewarded MRAID ads have a maximum of 30 seconds. '
                    + 'AppLovin supports '}/>
                <DocsPageModule.DocsPageContentCode children={'mraid.open'}/>
                <DocsPageModule.DocsPageContentText
                    text={' only for click trackers.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'Rewarded MRAID placements (playable ads) are off by default. '
                    + 'If you are interested in these, please contact your AppLovin point of contact.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Close Button'} type={DocsPageModule.DocsPageHeaderType.HEADING_3}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'Remove the close button if any. AppLovin supplies the close button on all MRAID ads.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'HTML Support'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'AppLovin RTB centers all HTML ads. '
                    + 'The resulting ad position depends on the ad size and the screen size. '
                    + 'AppLovin supplies the close button.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Use HTML snippets, not full HTML documents. Escape HTML ad markup.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'PNG, JPG/JPEG, and GIF are the supported media formats. '}/>
                <DocsPageModule.DocsPageContentAnchor to={'https://www.iab.com/guidelines/mraid/'} text={'MRAID'} newWindow={true} />
                <DocsPageModule.DocsPageContentText text={' 1.0, 2.0, and 3.0 are supported. '}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Native Support'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <DocsPageModule.DocsPageContent
                text={'AppLovin native ads use {OpenRTB/SPACE/Dynamic/SPACE/Native/SPACE/Ads/SPACE/API/SPACE/Specification/SPACE/Version/SPACE/1.2}(https://www.iab.com/wp-content/uploads/2018/03/OpenRTB-Native-Ads-Specification-Final-1.2.pdf) .'}/>

            <DocsPageModule.DocsPageContent
                text={'The impression object contains the native object. The native object contains the \`request\` field with the JSON string representation of the native request.'}/>

            <DocsPageModule.DocsPageContentText text={'Native Request Information'}
                                                fontFamily={LabelFontFamily.FONT_FAMILY_MEDIUM}/>

            <DataTable
                data={nativeRequestInformationData}
                columns={
                    [
                        {
                            id: 'field',
                            Header: 'Field',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.field}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                    ]
                }
                totalRowCount={nativeRequestInformationData.length}
            />

            <DocsPageModule.DocsPageContentSpace withSpace={IndentWidthType.SMALL} />
            <DocsPageModule.DocsPageContentText text={'Native Request Assets'}
                                                fontFamily={LabelFontFamily.FONT_FAMILY_MEDIUM}/>

            <DataTable
                data={nativeRequestAssetData}
                columns={
                    [
                        {
                            id: 'id',
                            Header: 'ID',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.id}/>
                        },
                        {
                            id: 'field',
                            Header: 'Field',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.field}/>
                        },
                        {
                            id: 'type',
                            Header: 'Type',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.type}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                    ]
                }
                totalRowCount={nativeRequestAssetData.length}
            />

            <DocsPageModule.DocsPageContentSpace withSpace={IndentWidthType.SMALL} />
            <DocsPageModule.DocsPageContentText text={'Native Response Information'}
                                                fontFamily={LabelFontFamily.FONT_FAMILY_MEDIUM}/>

            <DocsPageModule.DocsPageContent
                text={'Native response must have \`native.link.url\` with the destination link URL.'}/>

            <DocsPageModule.DocsPageContent
                text={'The AppLovin SDK does not yet support native video.'}/>

            <DocsPageModule.DocsPageHeader text={'CTV Support'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>
            <DocsPageModule.DocsPageContent
                text={'AppLovin RTB supports Connected TV (CTV) inventory. The following will help with a successful integration:' }/>
            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent
                        text={'CTV requests support video only, so there is no \`banner\` object under the \`imp\` object.'} />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent text={'VAST is the only supported format for Connected TV inventory. '
                                                        + 'AppLovin does not support MRAID or VPAID.'} />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent text={'Only the mp4 video MIME type is supported. '} />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent text={'\`seatbid.bid.ext.duration\` is mandatory.'} />
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageUnorderedList>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContent text={'For VAST \`InLine\` :'}/>
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent text={'\`bitrate\` should fall between \`minbitrate\` and \`maxbitrate\` .'} />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent text={'\`width\` and \`height\` should be equal to \`imp.video.w\` and \`imp.video.h\` in the bid request.'} />
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageUnorderedList>

            <DocsPageModule.DocsPageHeader text={'Ad Caching'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'The AppLovin SDK caches certain ads to improve ad display responsiveness. '
                    + 'Cached ads are shown only in the same session. '
                    + 'The SDK caches ads for up to four hours, but normally the period between cache and show is only a few minutes. '} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Video'} fontFamily={LabelFontFamily.FONT_FAMILY_MEDIUM}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageContent>
                    <DocsPageModule.DocsPageContentText
                        text={'AppLovin caches only inline video ads. '
                        + 'For optimal responsiveness, the maximum video file size allowed is 4MB. '
                        + 'Try to keep video file sizes below 2MB.'}/>
                </DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'MRAID'} fontFamily={LabelFontFamily.FONT_FAMILY_MEDIUM}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageContent>
                    <DocsPageModule.DocsPageContentText
                        text={ 'While AppLovin does not have a size limit, keep your creative assets in your MRAID markup as lean as possible to ensure your ads are responsive when the publisher attempts to render them. '
                             + 'All MRAID ads must include a loading indicator to signal when the ad is loading.' } />
                </DocsPageModule.DocsPageContent>
            </DocsPageModule.DocsPageUnorderedList>

        </>
    )
}

const DescriptionCellWrapper = styled( DocsPageModule.DocsPageContent )`
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  white-space: normal;
`;

export default ORTBCreativeTypesPage;
